import { useEffect, useRef, useState } from "react";
import api, { getNativePlayerUrl } from "./api";

// Attempts to hand off playback to Kodi via the streamhoardplay:// protocol, and detects
// whether anything actually caught it. Browsers give no real API for "did this custom
// protocol resolve" — the standard trick is: navigate to it, and if the OS switches away to
// launch a handler, the page loses focus (blur) almost immediately. If no blur fires within
// the timeout, nothing was registered to catch it, so treat it as "not installed" and show
// the install prompt instead of leaving the user staring at a blank tab forever.
export function useNativeLaunch() {
  const [status, setStatus] = useState("idle"); // idle | launching | launched | failed
  const [configReady, setConfigReady] = useState(false);
  const [lastPlayer, setLastPlayer] = useState("kodi"); // which player the last launch() targeted
  const lanBaseUrlRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    api.get("/config/native-player-url").then(({ data }) => {
      if (cancelled) return;
      lanBaseUrlRef.current = data.lan_base_url || "";
    }).catch(() => {}).finally(() => {
      // Ready either way — a failed fetch shouldn't block launching forever, it just means
      // this one attempt falls back to window.location.origin like before the LAN setting existed.
      if (!cancelled) setConfigReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // A ref (not state) for lanBaseUrl so `launch` always reads the latest value even when
  // called synchronously right after configReady flips true in the same render pass —
  // callers should still gate on `configReady` before auto-launching (see Player.jsx).
  const launch = (id, kind, player = "kodi") => {
    const url = getNativePlayerUrl(id, kind, lanBaseUrlRef.current, player);
    if (!url) return;
    setStatus("launching");
    setLastPlayer(player);
    window.location.href = url;
    // Don't trust a one-shot blur event alone — the browser's own "open external app?"
    // confirmation dialog also blurs the window, then returns focus once dismissed whether or
    // not anything actually launched. Instead check window focus at the deadline: a real
    // handoff to Kodi/the helper keeps this window unfocused for the whole window, while a
    // dialog interaction (or nothing at all) returns focus well before it.
    setTimeout(() => {
      setStatus(document.hasFocus() ? "failed" : "launched");
    }, 2000);
  };

  return { status, launch, configReady, lastPlayer };
}
